/**
 * EmergencyPatches.js
 * Push critical patches immediately to all/selected online devices.
 * Features:
 *  - Release date column
 *  - N-1 version logic (only latest + N-1 show highlighted Deploy button)
 *  - Multi-select patches for bulk deploy
 *  - Per-patch or bulk target selection
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

const SEV_COLOR = { critical: '#dc2626', high: '#ea580c', medium: '#d97706', low: '#16a34a' };
const SEV_BG    = { critical: '#fee2e2', high: '#ffedd5', medium: '#fef3c7', low: '#dcfce7' };
const CAT_ICON  = { os: '🪟', browser: '🌐', application: '📦', driver: '🔧', bios: '⚡', firmware: '💾' };
const STS_COLOR = { success: '#16a34a', failed: '#dc2626', pending: '#d97706', in_progress: '#1d4ed8' };
const STS_BG    = { success: '#dcfce7', failed: '#fee2e2', pending: '#fef3c7', in_progress: '#dbeafe' };

function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
}

// ── Version comparison helpers ─────────────────────────────────────────────
function parseVer(v) {
  if (!v) return [0];
  return String(v).split(/[.\-]/).map(n => parseInt(n) || 0);
}
function cmpVer(a, b) {
  const pa = parseVer(a), pb = parseVer(b);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d !== 0) return d;
  }
  return 0;
}

/**
 * For each vendor+product group, mark patches as:
 *  'latest' — highest version
 *  'n-1'    — second highest version (one behind latest)
 *  'old'    — anything older
 * Returns a Map: patchId → 'latest'|'n-1'|'old'
 */
function computeVersionRanks(patches) {
  // Group by vendor+product
  const groups = {};
  patches.forEach(p => {
    const key = `${p.vendor}||${p.product}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
  });

  const ranks = new Map();
  Object.values(groups).forEach(group => {
    // Sort descending by version
    const sorted = [...group].sort((a, b) => cmpVer(b.version, a.version));
    sorted.forEach((p, idx) => {
      if (idx === 0) ranks.set(p.id, 'latest');
      else if (idx === 1) ranks.set(p.id, 'n-1');
      else ranks.set(p.id, 'old');
    });
  });
  return ranks;
}

export default function EmergencyPatches({ user }) {
  const [patches,   setPatches]   = useState([]);
  const [devices,   setDevices]   = useState([]);
  const [history,   setHistory]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState('deploy');

  // Multi-select state
  const [selectedPatches, setSelectedPatches] = useState(new Set());

  // Single-patch modal
  const [showModal,    setShowModal]    = useState(false);
  const [selPatch,     setSelPatch]     = useState(null);
  const [selDevices,   setSelDevices]   = useState([]);
  const [targeting,    setTargeting]    = useState('all');
  const [deploying,    setDeploying]    = useState(false);
  const [deployResult, setDeployResult] = useState(null);
  const [liveStatus,   setLiveStatus]   = useState(null);
  const [polling,      setPolling]      = useState(false);

  // Bulk deploy modal
  const [showBulkModal,  setShowBulkModal]  = useState(false);
  const [bulkTargeting,  setBulkTargeting]  = useState('all');
  const [bulkSelDevices, setBulkSelDevices] = useState([]);
  const [bulkDeploying,  setBulkDeploying]  = useState(false);
  const [bulkResult,     setBulkResult]     = useState(null);

  const [search, setSearch] = useState('');
  const [sevFilter, setSevFilter] = useState('all');
  const [rankFilter, setRankFilter] = useState('active'); // active = latest+n-1 only | all
  const [msg,    setMsg]    = useState({ text: '', type: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, dRes, cRes] = await Promise.all([
        api.getAllPatches(),
        api.getConnectionStatus(),
        api.getCompliance()
      ]);
      // Enrich patches with release date from patch_releases
      setPatches(pRes.data);
      setDevices(dRes.data.devices || []);
      const details = cRes.data.details || [];
      setHistory(details.filter(s => s.isEmergency));
    } catch (e) { flash('Failed to load: ' + e.message, 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  function flash(text, type = 'success') {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 5000);
  }

  // ── Single patch deploy ────────────────────────────────────────────────
  function openModal(patch) {
    setSelPatch(patch);
    setSelDevices([]);
    setTargeting('all');
    setDeployResult(null);
    setLiveStatus(null);
    setShowModal(true);
  }

  async function deploy() {
    if (!selPatch) return;
    setDeploying(true);
    setDeployResult(null);
    try {
      const deviceIds = targeting === 'selected' ? selDevices : undefined;
      const res = await api.triggerEmergency(selPatch.id, deviceIds, user?.username || 'admin');
      setDeployResult(res.data);
      flash(`🚨 Emergency patch deployed to ${res.data.assigned} device(s)`, 'success');
      startPolling(selPatch.id);
    } catch (e) {
      flash('Deploy failed: ' + (e.response?.data?.error || e.message), 'error');
    } finally { setDeploying(false); }
  }

  function startPolling(patchId) {
    setPolling(true);
    const interval = setInterval(async () => {
      try {
        const res = await api.getEmergencyStatus(patchId);
        setLiveStatus(res.data);
        const { summary } = res.data;
        if (summary.pending === 0 && summary.in_progress === 0) {
          clearInterval(interval); setPolling(false); load();
        }
      } catch { clearInterval(interval); setPolling(false); }
    }, 5000);
    setTimeout(() => { clearInterval(interval); setPolling(false); }, 600000);
  }

  // ── Bulk deploy ────────────────────────────────────────────────────────
  async function bulkDeploy() {
    if (selectedPatches.size === 0) return;
    setBulkDeploying(true);
    setBulkResult(null);
    let totalAssigned = 0, failed = 0;
    try {
      const deviceIds = bulkTargeting === 'selected' ? bulkSelDevices : undefined;
      for (const patchId of selectedPatches) {
        try {
          const res = await api.triggerEmergency(patchId, deviceIds, user?.username || 'admin');
          totalAssigned += res.data.assigned || 0;
        } catch { failed++; }
      }
      setBulkResult({ assigned: totalAssigned, failed, total: selectedPatches.size });
      flash(`🚨 Bulk deploy: ${selectedPatches.size} patches → ${totalAssigned} device deployments`, 'success');
      setSelectedPatches(new Set());
      load();
    } catch (e) {
      flash('Bulk deploy failed: ' + e.message, 'error');
    } finally { setBulkDeploying(false); }
  }

  // ── Version ranking ────────────────────────────────────────────────────
  const versionRanks = computeVersionRanks(patches);

  const onlineDevices = devices.filter(d => d.connectionHealth === 'healthy');

  const filteredPatches = patches.filter(p => {
    if (search && ![p.name, p.vendor, p.category].join(' ').toLowerCase().includes(search.toLowerCase())) return false;
    if (sevFilter !== 'all' && p.severity !== sevFilter) return false;
    if (rankFilter === 'active') {
      const r = versionRanks.get(p.id);
      if (r !== 'latest' && r !== 'n-1') return false;
    }
    return true;
  });

  const selectedCount = selectedPatches.size;
  const isAdmin = user?.role === 'admin';

  function togglePatch(id) {
    setSelectedPatches(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selectedPatches.size === filteredPatches.length) {
      setSelectedPatches(new Set());
    } else {
      setSelectedPatches(new Set(filteredPatches.map(p => p.id)));
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title">🚨 Emergency Patches</div>
          <div className="page-subtitle">
            Push critical patches immediately · Latest &amp; N-1 versions highlighted
          </div>
        </div>
        <div className="flex-gap">
          {isAdmin && selectedCount > 0 && (
            <button
              className="btn btn-danger"
              onClick={() => { setBulkResult(null); setBulkTargeting('all'); setBulkSelDevices([]); setShowBulkModal(true); }}
            >
              🚨 Deploy Selected ({selectedCount})
            </button>
          )}
          <button className="btn btn-ghost" onClick={load} disabled={loading}>↻ Refresh</button>
        </div>
      </div>

      {msg.text && (
        <div className={`alert ${msg.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 16 }}>
          {msg.text}
        </div>
      )}

      <div className="alert info" style={{ marginBottom: 20 }}>
        🚨 <strong>Emergency patches bypass the 10-minute poll cycle.</strong> Agents receive the task
        within 60 seconds. <strong>Latest</strong> and <strong>N-1</strong> versions are highlighted —
        older versions are hidden by default. Use multi-select to deploy multiple patches at once.
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e8ecf4', marginBottom: 20 }}>
        {[
          { id: 'deploy',  label: '🚀 Deploy Emergency Patch' },
          { id: 'history', label: `📋 History (${history.length})` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '10px 20px', fontSize: 13, fontWeight: 600,
            color: tab === t.id ? '#dc2626' : '#94a3b8',
            borderBottom: tab === t.id ? '2px solid #dc2626' : '2px solid transparent',
            marginBottom: -2
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── DEPLOY TAB ── */}
      {tab === 'deploy' && (
        <>
          {/* KPI cards */}
          <div className="cards-row" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 20 }}>
            <div className="card green" style={{ cursor:'pointer' }}
              onClick={() => { setRankFilter('active'); setSevFilter('all'); }}>
              <div className="card-label">Online Endpoints</div>
              <div className="card-value">{onlineDevices.length}</div>
              <div className="card-sub">ready to receive</div>
            </div>
            <div className="card red"
              style={{ cursor:'pointer', outline: sevFilter==='critical'?'2px solid #dc2626':undefined }}
              onClick={() => setSevFilter(sevFilter==='critical'?'all':'critical')}>
              <div className="card-label">Critical Patches</div>
              <div className="card-value">{patches.filter(p => p.severity === 'critical').length}</div>
              <div className="card-sub">{sevFilter==='critical' ? '▼ filtered' : 'click to filter'}</div>
            </div>
            <div className="card amber"
              style={{ cursor:'pointer', outline: rankFilter==='active'?'2px solid #d97706':undefined }}
              onClick={() => setRankFilter(rankFilter==='active'?'all':'active')}>
              <div className="card-label">Latest Versions</div>
              <div className="card-value">{[...versionRanks.values()].filter(r => r === 'latest').length}</div>
              <div className="card-sub">{rankFilter==='active' ? '▼ latest+N-1' : 'click to filter'}</div>
            </div>
            <div className="card blue">
              <div className="card-label">Selected</div>
              <div className="card-value">{selectedCount}</div>
              <div className="card-sub">for bulk deploy</div>
            </div>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              className="form-input"
              style={{ maxWidth: 240 }}
              placeholder="Search patches..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {/* Version filter */}
            <div style={{ display: 'flex', gap: 6 }}>
              {[
                { id: 'active', label: '⚡ Latest + N-1' },
                { id: 'all',    label: 'All Versions' },
              ].map(f => (
                <button key={f.id}
                  className={`btn btn-sm ${rankFilter === f.id ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setRankFilter(f.id)}>
                  {f.label}
                </button>
              ))}
            </div>
            {/* Severity filter */}
            <div style={{ display: 'flex', gap: 6 }}>
              {['all','critical','high','medium'].map(s => (
                <button key={s}
                  className={`btn btn-sm ${sevFilter === s ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setSevFilter(s)}
                  style={sevFilter === s && s !== 'all' ? { background: SEV_COLOR[s], color: '#fff', border: 'none' } : {}}>
                  {s === 'all' ? 'All Severity' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8' }}>
              {filteredPatches.length} patch{filteredPatches.length !== 1 ? 'es' : ''}
            </span>
          </div>

          {/* Patch table */}
          <div className="panel">
            <div className="panel-header">
              <div className="panel-title">
                <div className="panel-title-icon">📦</div>
                Select Patch(es) to Deploy as Emergency
              </div>
              {isAdmin && filteredPatches.length > 0 && (
                <button className="btn btn-sm btn-outline" onClick={toggleAll}>
                  {selectedPatches.size === filteredPatches.length ? '☑ Deselect All' : '☐ Select All'}
                </button>
              )}
            </div>
            {loading ? <div className="loading">Loading...</div> : (
              <table>
                <thead>
                  <tr>
                    {isAdmin && <th style={{ width: 36 }}></th>}
                    <th style={{ width: 36 }}></th>
                    <th>Patch Name</th>
                    <th>Vendor / Product</th>
                    <th>Version</th>
                    <th>Release Date</th>
                    <th>Severity</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPatches.length === 0 && (
                    <tr><td colSpan={isAdmin ? 9 : 8} className="empty">No patches found.</td></tr>
                  )}
                  {filteredPatches.map(p => {
                    const rank = versionRanks.get(p.id);
                    const isLatest = rank === 'latest';
                    const isN1     = rank === 'n-1';
                    const isActive = isLatest || isN1;
                    const isSelected = selectedPatches.has(p.id);

                    return (
                      <tr key={p.id} style={{
                        background: isSelected ? '#eff4ff' : isLatest ? '#fff8f0' : isN1 ? '#fffbeb' : '#fff',
                        borderLeft: isLatest ? '3px solid #dc2626' : isN1 ? '3px solid #ea580c' : '3px solid transparent'
                      }}>
                        {isAdmin && (
                          <td>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => togglePatch(p.id)}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                        )}
                        <td style={{ textAlign: 'center', fontSize: 18 }}>
                          {CAT_ICON[p.category] || '📦'}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#1e2a3b' }}>{p.name}</div>
                          {p.description && (
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                              {p.description.substring(0, 60)}{p.description.length > 60 ? '…' : ''}
                            </div>
                          )}
                        </td>
                        <td>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{p.vendor}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{p.product || p.category}</div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1e2a3b' }}>
                              {p.version}
                            </span>
                            {isLatest && (
                              <span style={{ fontSize: 10, fontWeight: 700, background: '#dc2626',
                                color: '#fff', padding: '1px 6px', borderRadius: 10 }}>LATEST</span>
                            )}
                            {isN1 && (
                              <span style={{ fontSize: 10, fontWeight: 700, background: '#ea580c',
                                color: '#fff', padding: '1px 6px', borderRadius: 10 }}>N-1</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div style={{ fontSize: 12, color: '#374151' }}>
                            {fmtDate(p.releaseDate)}
                          </div>
                          {p.lastChecked && (
                            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>
                              checked {fmtDate(p.lastChecked)}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className="badge" style={{ background: SEV_BG[p.severity], color: SEV_COLOR[p.severity] }}>
                            {p.severity}
                          </span>
                        </td>
                        <td>
                          {isActive ? (
                            <span style={{ fontSize: 11, fontWeight: 600,
                              color: isLatest ? '#dc2626' : '#ea580c',
                              background: isLatest ? '#fee2e2' : '#ffedd5',
                              padding: '2px 8px', borderRadius: 10 }}>
                              {isLatest ? '⚡ Current' : '↩ N-1'}
                            </span>
                          ) : (
                            <span style={{ fontSize: 11, color: '#94a3b8' }}>Superseded</span>
                          )}
                        </td>
                        <td>
                          {isAdmin ? (
                            <button
                              className="btn btn-sm"
                              onClick={() => openModal(p)}
                              style={{
                                background: isActive ? '#dc2626' : '#e2e8f0',
                                color: isActive ? '#fff' : '#94a3b8',
                                border: 'none',
                                fontWeight: isActive ? 700 : 400,
                                boxShadow: isActive ? '0 2px 8px rgba(220,38,38,0.3)' : 'none',
                                cursor: 'pointer'
                              }}
                            >
                              {isActive ? '🚨 Deploy Emergency' : 'Deploy'}
                            </button>
                          ) : (
                            <span style={{ fontSize: 11, color: '#94a3b8' }}>Admin only</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Bulk deploy bar */}
          {isAdmin && selectedCount > 0 && (
            <div style={{
              position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
              background: '#0f2350', color: '#fff', borderRadius: 12,
              padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 16,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)', zIndex: 200, minWidth: 400
            }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                🚨 {selectedCount} patch{selectedCount !== 1 ? 'es' : ''} selected
              </span>
              <button
                className="btn btn-sm"
                style={{ background: '#dc2626', color: '#fff', border: 'none', fontWeight: 700 }}
                onClick={() => { setBulkResult(null); setBulkTargeting('all'); setBulkSelDevices([]); setShowBulkModal(true); }}
              >
                Deploy All Selected
              </button>
              <button
                className="btn btn-sm"
                style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none' }}
                onClick={() => setSelectedPatches(new Set())}
              >
                Clear
              </button>
            </div>
          )}
        </>
      )}

      {/* ── HISTORY TAB ── */}
      {tab === 'history' && (
        <div className="panel">
          <div className="panel-header">
            <div className="panel-title">
              <div className="panel-title-icon">📋</div>
              Emergency Deployment History
            </div>
          </div>
          {history.length === 0 ? (
            <div className="empty">No emergency deployments yet.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Device</th><th>Patch</th><th>Version</th>
                  <th>Status</th><th>Completed</th><th>Output</th>
                </tr>
              </thead>
              <tbody>
                {history.map((s, i) => (
                  <tr key={i}>
                    <td><strong>{s.device?.hostname || '—'}</strong></td>
                    <td style={{ fontSize: 13 }}>{s.patch?.name || '—'}</td>
                    <td style={{ fontSize: 12, color: '#94a3b8' }}>{s.patch?.version || '—'}</td>
                    <td>
                      <span className="badge" style={{ background: STS_BG[s.status], color: STS_COLOR[s.status] }}>
                        🚨 {s.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: '#94a3b8' }}>{fmtDateTime(s.completedAt)}</td>
                    <td style={{ fontSize: 11, color: '#94a3b8', maxWidth: 200 }}>
                      {s.error ? s.error.substring(0, 80) : s.output?.substring(0, 80) || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── SINGLE DEPLOY MODAL ── */}
      {showModal && selPatch && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:32, width:560,
            maxHeight:'85vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>

            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
              <div style={{ width:40, height:40, borderRadius:8, background:'#fee2e2',
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>
                🚨
              </div>
              <div>
                <div style={{ fontSize:16, fontWeight:700, color:'#1e2a3b' }}>Emergency Patch Deployment</div>
                <div style={{ fontSize:12, color:'#94a3b8' }}>Bypasses normal scheduling — agents receive within 60s</div>
              </div>
              <button onClick={() => setShowModal(false)}
                style={{ marginLeft:'auto', background:'none', border:'none', fontSize:20,
                  cursor:'pointer', color:'#94a3b8' }}>×</button>
            </div>

            {/* Patch info card */}
            <div style={{ background:'#f8fafc', borderRadius:8, padding:16, marginBottom:20,
              border:`2px solid ${SEV_BG[selPatch.severity] || '#e8ecf4'}` }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                <span style={{ fontSize:22 }}>{CAT_ICON[selPatch.category] || '📦'}</span>
                <div>
                  <div style={{ fontWeight:700, fontSize:14 }}>{selPatch.name}</div>
                  <div style={{ fontSize:12, color:'#64748b' }}>{selPatch.vendor} · {selPatch.product}</div>
                </div>
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                <span className="badge" style={{ background:SEV_BG[selPatch.severity], color:SEV_COLOR[selPatch.severity] }}>
                  {selPatch.severity}
                </span>
                <span style={{ fontSize:12, color:'#1e2a3b', fontWeight:600 }}>v{selPatch.version}</span>
                {(() => {
                  const r = versionRanks.get(selPatch.id);
                  return r === 'latest'
                    ? <span style={{ fontSize:10, fontWeight:700, background:'#dc2626', color:'#fff', padding:'2px 8px', borderRadius:10 }}>LATEST</span>
                    : r === 'n-1'
                    ? <span style={{ fontSize:10, fontWeight:700, background:'#ea580c', color:'#fff', padding:'2px 8px', borderRadius:10 }}>N-1</span>
                    : null;
                })()}
                {selPatch.releaseDate && (
                  <span style={{ fontSize:11, color:'#94a3b8' }}>Released: {fmtDate(selPatch.releaseDate)}</span>
                )}
              </div>
            </div>

            {/* Target selection */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:13, fontWeight:600, marginBottom:10, color:'#1e2a3b' }}>Target Endpoints</div>
              <div style={{ display:'flex', gap:10, marginBottom:12 }}>
                {[
                  { id:'all',      label:`All Online (${onlineDevices.length})` },
                  { id:'selected', label:'Select Specific' },
                ].map(t => (
                  <button key={t.id}
                    className={`btn btn-sm ${targeting === t.id ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setTargeting(t.id)}>
                    {t.label}
                  </button>
                ))}
              </div>
              {targeting === 'selected' && (
                <div style={{ border:'1px solid #e8ecf4', borderRadius:8, overflow:'hidden', maxHeight:220, overflowY:'auto' }}>
                  {onlineDevices.map(d => (
                    <label key={d.id} style={{
                      display:'flex', alignItems:'center', gap:10,
                      padding:'10px 14px', cursor:'pointer',
                      borderBottom:'1px solid #f0f4ff',
                      background: selDevices.includes(d.id) ? '#eff4ff' : '#fff'
                    }}>
                      <input type="checkbox" checked={selDevices.includes(d.id)}
                        onChange={e => setSelDevices(prev =>
                          e.target.checked ? [...prev, d.id] : prev.filter(x => x !== d.id)
                        )}
                      />
                      <div>
                        <div style={{ fontSize:13, fontWeight:600 }}>{d.hostname}</div>
                        <div style={{ fontSize:11, color:'#94a3b8' }}>{d.os} · {d.ipAddress}</div>
                      </div>
                      <span className="badge online" style={{ marginLeft:'auto' }}>● Online</span>
                    </label>
                  ))}
                  {onlineDevices.length === 0 && <div className="empty" style={{ padding:20 }}>No online devices</div>}
                </div>
              )}
            </div>

            {deployResult && (
              <div className="alert success" style={{ marginBottom:16 }}>
                ✔ Deployed to {deployResult.assigned} device(s) · {deployResult.skipped} skipped
              </div>
            )}

            {liveStatus && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:13, fontWeight:600, marginBottom:8, color:'#1e2a3b' }}>
                  Live Status {polling && <span style={{ color:'#1a56db' }}>⟳ updating...</span>}
                </div>
                <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:10 }}>
                  {Object.entries(liveStatus.summary).map(([k,v]) => (
                    <div key={k} style={{ textAlign:'center', minWidth:60 }}>
                      <div style={{ fontSize:20, fontWeight:700,
                        color: k==='success'?'#16a34a':k==='failed'?'#dc2626':'#d97706' }}>{v}</div>
                      <div style={{ fontSize:10, color:'#94a3b8', textTransform:'uppercase' }}>{k}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  {liveStatus.statuses.map((s, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:8,
                      padding:'6px 10px', background:'#f8fafc', borderRadius:6 }}>
                      <span className="badge" style={{ background:STS_BG[s.status], color:STS_COLOR[s.status], fontSize:10 }}>
                        {s.status}
                      </span>
                      <span style={{ fontSize:13, fontWeight:500 }}>{s.device?.hostname}</span>
                      <span style={{ fontSize:11, color:'#94a3b8', marginLeft:'auto' }}>{s.device?.ipAddress}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex-gap" style={{ justifyContent:'flex-end', marginTop:8 }}>
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>
                {deployResult ? 'Close' : 'Cancel'}
              </button>
              {!deployResult && (
                <button className="btn btn-danger" onClick={deploy}
                  disabled={deploying || (targeting==='selected' && selDevices.length===0)}>
                  {deploying ? '⟳ Deploying...' :
                    `🚨 Deploy to ${targeting==='all' ? `All ${onlineDevices.length}` : selDevices.length} Device(s)`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── BULK DEPLOY MODAL ── */}
      {showBulkModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:32, width:520,
            maxHeight:'85vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>

            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
              <div style={{ width:40, height:40, borderRadius:8, background:'#fee2e2',
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>🚨</div>
              <div>
                <div style={{ fontSize:16, fontWeight:700, color:'#1e2a3b' }}>
                  Bulk Emergency Deployment
                </div>
                <div style={{ fontSize:12, color:'#94a3b8' }}>
                  Deploying {selectedCount} patch{selectedCount !== 1 ? 'es' : ''} simultaneously
                </div>
              </div>
              <button onClick={() => setShowBulkModal(false)}
                style={{ marginLeft:'auto', background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#94a3b8' }}>×</button>
            </div>

            {/* Selected patches list */}
            <div style={{ background:'#f8fafc', borderRadius:8, padding:12, marginBottom:20,
              border:'1px solid #e8ecf4', maxHeight:160, overflowY:'auto' }}>
              <div style={{ fontSize:12, fontWeight:600, color:'#64748b', marginBottom:8 }}>
                Selected Patches ({selectedCount})
              </div>
              {patches.filter(p => selectedPatches.has(p.id)).map(p => {
                const rank = versionRanks.get(p.id);
                return (
                  <div key={p.id} style={{ display:'flex', alignItems:'center', gap:8,
                    padding:'4px 0', borderBottom:'1px solid #f0f4ff' }}>
                    <span style={{ fontSize:14 }}>{CAT_ICON[p.category] || '📦'}</span>
                    <span style={{ fontSize:12, fontWeight:600, flex:1 }}>{p.name}</span>
                    <span style={{ fontSize:11, color:'#94a3b8' }}>v{p.version}</span>
                    {rank === 'latest' && <span style={{ fontSize:10, fontWeight:700, background:'#dc2626', color:'#fff', padding:'1px 6px', borderRadius:10 }}>LATEST</span>}
                    {rank === 'n-1' && <span style={{ fontSize:10, fontWeight:700, background:'#ea580c', color:'#fff', padding:'1px 6px', borderRadius:10 }}>N-1</span>}
                    <span className="badge" style={{ background:SEV_BG[p.severity], color:SEV_COLOR[p.severity], fontSize:10 }}>{p.severity}</span>
                  </div>
                );
              })}
            </div>

            {/* Target selection */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:13, fontWeight:600, marginBottom:10, color:'#1e2a3b' }}>Target Endpoints</div>
              <div style={{ display:'flex', gap:10, marginBottom:12 }}>
                {[
                  { id:'all',      label:`All Online (${onlineDevices.length})` },
                  { id:'selected', label:'Select Specific' },
                ].map(t => (
                  <button key={t.id}
                    className={`btn btn-sm ${bulkTargeting === t.id ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setBulkTargeting(t.id)}>
                    {t.label}
                  </button>
                ))}
              </div>
              {bulkTargeting === 'selected' && (
                <div style={{ border:'1px solid #e8ecf4', borderRadius:8, overflow:'hidden', maxHeight:180, overflowY:'auto' }}>
                  {onlineDevices.map(d => (
                    <label key={d.id} style={{
                      display:'flex', alignItems:'center', gap:10,
                      padding:'8px 14px', cursor:'pointer',
                      borderBottom:'1px solid #f0f4ff',
                      background: bulkSelDevices.includes(d.id) ? '#eff4ff' : '#fff'
                    }}>
                      <input type="checkbox" checked={bulkSelDevices.includes(d.id)}
                        onChange={e => setBulkSelDevices(prev =>
                          e.target.checked ? [...prev, d.id] : prev.filter(x => x !== d.id)
                        )}
                      />
                      <div>
                        <div style={{ fontSize:13, fontWeight:600 }}>{d.hostname}</div>
                        <div style={{ fontSize:11, color:'#94a3b8' }}>{d.os} · {d.ipAddress}</div>
                      </div>
                      <span className="badge online" style={{ marginLeft:'auto' }}>● Online</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {bulkResult && (
              <div className="alert success" style={{ marginBottom:16 }}>
                ✔ {bulkResult.total} patches deployed · {bulkResult.assigned} total device assignments
                {bulkResult.failed > 0 && <span style={{ color:'#dc2626' }}> · {bulkResult.failed} failed</span>}
              </div>
            )}

            <div className="flex-gap" style={{ justifyContent:'flex-end', marginTop:8 }}>
              <button className="btn btn-ghost" onClick={() => setShowBulkModal(false)}>
                {bulkResult ? 'Close' : 'Cancel'}
              </button>
              {!bulkResult && (
                <button className="btn btn-danger" onClick={bulkDeploy}
                  disabled={bulkDeploying || (bulkTargeting==='selected' && bulkSelDevices.length===0)}>
                  {bulkDeploying ? '⟳ Deploying...' :
                    `🚨 Deploy ${selectedCount} Patches to ${bulkTargeting==='all' ? `All ${onlineDevices.length}` : bulkSelDevices.length} Device(s)`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
